import React, { useState } from 'react';
import { sendPasswordResetEmail } from '../../services/authService';

type Tab = 'account' | 'password';

interface AuthAccountRecoveryModalProps {
  open: boolean;
  onClose: () => void;
}

const AuthAccountRecoveryModal: React.FC<AuthAccountRecoveryModalProps> = ({ open, onClose }) => {
  const [tab, setTab] = useState<Tab>('account');
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (!open) return null;

  const trimmed = email.trim();
  const canSubmit = trimmed.length > 3 && trimmed.includes('@');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setMessage(null);
    if (!canSubmit) {
      setError('가입 시 사용한 이메일을 입력해 주세요.');
      return;
    }
    setBusy(true);
    try {
      await sendPasswordResetEmail(trimmed);
      setMessage(
        tab === 'password'
          ? '비밀번호 재설정 안내 메일을 보냈습니다. 메일함과 스팸함을 확인해 주세요.'
          : '입력하신 주소로 안내 메일을 보냈습니다. 메일함과 스팸함을 확인해 주세요. (이메일 가입 계정인 경우 비밀번호를 재설정할 수 있습니다.)'
      );
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : '요청을 처리하지 못했습니다.';
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  const switchTab = (next: Tab) => {
    setTab(next);
    setMessage(null);
    setError(null);
  };

  return (
    <div
      className="cp-modal-backdrop cp-auth-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cp-auth-recovery-title"
      onClick={onClose}
    >
      <div className="cp-modal cp-auth-modal" onClick={(e) => e.stopPropagation()}>
        <h2 id="cp-auth-recovery-title" className="cp-auth-modal-title">
          계정/비밀번호 찾기
        </h2>
        <div className="cp-auth-modal-tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'account'}
            className={`cp-auth-modal-tab${tab === 'account' ? ' is-active' : ''}`}
            onClick={() => switchTab('account')}
          >
            계정 찾기
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'password'}
            className={`cp-auth-modal-tab${tab === 'password' ? ' is-active' : ''}`}
            onClick={() => switchTab('password')}
          >
            비밀번호 찾기
          </button>
        </div>
        <p className="cp-auth-modal-hint">
          {tab === 'password'
            ? '가입 시 사용한 이메일 주소를 입력하시면 비밀번호 재설정 링크를 보내드립니다.'
            : '가입 시 사용한 이메일 주소를 입력하시면 해당 이메일로 가입된 계정이 있을 때 비밀번호 재설정 안내를 보내드립니다.'}
        </p>
        <form onSubmit={handleSubmit} className="cp-auth-modal-form">
          <label className="cp-auth-modal-label" htmlFor="cp-auth-recovery-email">
            이메일 주소
          </label>
          <input
            id="cp-auth-recovery-email"
            type="email"
            className="cp-auth-modal-input"
            autoComplete="email"
            placeholder="가입 시 사용한 이메일을 입력하세요"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          {message && <p className="cp-auth-modal-success">{message}</p>}
          {error && <p className="cp-auth-modal-error">{error}</p>}
          <div className="cp-modal-footer cp-leave-modal-actions">
            <button type="button" className="cp-btn-secondary" onClick={onClose}>
              취소
            </button>
            <button type="submit" className="cp-btn-primary" disabled={!canSubmit || busy}>
              {tab === 'password' ? (busy ? '전송 중…' : '비밀번호 재설정') : busy ? '전송 중…' : '계정 찾기'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AuthAccountRecoveryModal;
